import Modal from "common/Modal/Modal";
import { useEffect, useState } from "react";
import { DarkModeNotifications, Notifications } from "common/Icons/Icons";
import styled, { useTheme } from "styled-components";
import Text from "common/Typography/Text";
import { LOCAL_STORAGE_TG_LAST_WRITE_ACCESS_KEY } from "common/constants";
import Flex from "common/Boxes/Flex";
import Button from "common/Buttons/Button";
import { sharedLib } from "@shared/services/shared.service";
import { telegramRequestWriteAccess } from "features/telegram/utils.ts";
import { WebAppUser } from "@shared/types";
import { useTelegramWebAppData } from "@shared/utils/hooks/misc.ts";

const TelegramNotificationsModal = () => {
  const theme = useTheme();
  const { telegramInitData } = useTelegramWebAppData();

  const getTelegramUserInitData = (): WebAppUser | undefined => {
    try {
      const query = new URLSearchParams(telegramInitData);
      const userData = query.get("user") || "";
      return JSON.parse(userData);
    } catch {}
  };

  const userInitData = getTelegramUserInitData();
  const userDisabledWrite = userInitData?.allows_write_to_pm === false;
  const storageLastRequest = localStorage?.getItem(
    LOCAL_STORAGE_TG_LAST_WRITE_ACCESS_KEY
  );
  const blockRequestWrite =
    !!storageLastRequest &&
    Number(storageLastRequest) + TWO_WEEKS_MS > Date.now();
  const [isOpen, setIsOpen] = useState(!blockRequestWrite && userDisabledWrite);
  const [setAllowSendingPm] =
    sharedLib.sharedApi.common.useSetAllowSendingPmMutation();

  const updateAllowSendingPm = (allowSendingPm: any) => {
    if (typeof allowSendingPm === "boolean") {
      setAllowSendingPm({ allow_sending_pm: allowSendingPm });
    }
  };

  const requestWriteAccess = () => {
    telegramRequestWriteAccess(updateAllowSendingPm);
  };

  const updateLocalStorage = () => {
    localStorage?.setItem(
      LOCAL_STORAGE_TG_LAST_WRITE_ACCESS_KEY,
      Date.now().toString()
    );
  };

  useEffect(() => {
    if (blockRequestWrite) return;
    if (!userDisabledWrite) {
      requestWriteAccess();
    }
    updateLocalStorage();
  }, []);

  const closeModal = () => setIsOpen(false);

  const onCancel = () => {
    closeModal();
    if (userDisabledWrite) {
      updateAllowSendingPm(false);
    }
  };

  const onOk = () => {
    closeModal();
    requestWriteAccess();
  };

  const footer = (
    <Flex $justifyContent="flex-end">
      <Button type="link" onClick={onCancel}>
        Ask me later
      </Button>
      <Button type="primary" onClick={onOk} style={{ marginLeft: 8 }}>
        Continue
      </Button>
    </Flex>
  );

  const Icon = theme.isDarkMode ? DarkModeNotifications : Notifications;

  return (
    <Modal open={isOpen} footer={footer} closable={false}>
      <ContentWrapper>
        <Icon style={{ width: 120, height: "auto", marginBottom: 24 }} />
        <Text $size="L" style={{ marginBottom: 4 }}>
          Allow Our Bot to Send You Messages
        </Text>
        <Text type="secondary">
          Enable our bot to send you trade alerts, updates, and important
          messages directly to your inbox. Stay informed and make the most of
          your trading experience.
        </Text>
      </ContentWrapper>
    </Modal>
  );
};

export default TelegramNotificationsModal;

const TWO_WEEKS_MS = 1000 * 60 * 60 * 24 * 14;

const ContentWrapper = styled.div`
  text-align: center;
  max-width: 372px;
  margin: 0 auto;
`;
